import React, { useState, useRef, useEffect } from 'react';

/**
 * OTP VERIFICATION - PART 3
 *
 * Check if code matches and is valid
 * See EVERYTHING about verification process
 */

interface VerificationEvent {
  id: string;
  timestamp: number;
  layer: 'input' | 'validation' | 'database' | 'comparison' | 'expiry' | 'result';
  type: 'info' | 'success' | 'error' | 'process' | 'detail' | 'warning';
  message: string;
  details?: any;
  duration?: number;
}

interface VerificationAttempt {
  id: string;
  email: string;
  codeEntered: string;
  codeStored?: string;
  result: 'success' | 'invalid' | 'expired' | 'used' | 'not_found';
  timestamp: Date;
  processingTime: number;
}

export default function OTPVerification() {
  const [events, setEvents] = useState<VerificationEvent[]>([]);
  const [isVerifying, setIsVerifying] = useState(false);
  const [email, setEmail] = useState('test@example.com');
  const [enteredCode, setEnteredCode] = useState('');
  const [attempts, setAttempts] = useState<VerificationAttempt[]>([]);
  const [lastResult, setLastResult] = useState<'success' | 'failure' | null>(null);

  // Simulated database
  const [mockDatabase] = useState([
    { email: 'test@example.com', code: '123456', expiresAt: new Date(Date.now() + 10 * 60 * 1000), used: false },
    { email: 'expired@example.com', code: '999999', expiresAt: new Date(Date.now() - 60 * 1000), used: false },
    { email: 'used@example.com', code: '111111', expiresAt: new Date(Date.now() + 10 * 60 * 1000), used: true }
  ]);

  const codeInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const eventsEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    eventsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [events]);

  const logEvent = (event: Omit<VerificationEvent, 'id' | 'timestamp'>) => {
    const newEvent: VerificationEvent = {
      ...event,
      id: `evt-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: Date.now()
    };

    setEvents(prev => [...prev, newEvent]);
    return new Promise(resolve => setTimeout(resolve, event.duration || 50));
  };

  const handleCodeInput = (index: number, value: string) => {
    if (value.length <= 1) {
      const newCode = enteredCode.split('');
      newCode[index] = value;
      setEnteredCode(newCode.join(''));

      // Auto-focus next input
      if (value && index < 5) {
        codeInputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !enteredCode[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
  };

  const verifyOTP = async () => {
    setIsVerifying(true);
    setEvents([]);
    setLastResult(null);
    const startTime = Date.now();

    try {
      // Step 1: Input validation
      await logEvent({
        layer: 'input',
        type: 'info',
        message: 'Validating input format',
        details: {
          email,
          codeEntered: enteredCode,
          codeLength: enteredCode.length,
          isNumeric: /^\d+$/.test(enteredCode),
          emailValid: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
        }
      });

      if (enteredCode.length !== 6) {
        await logEvent({
          layer: 'validation',
          type: 'error',
          message: 'Invalid code length',
          details: {
            expected: 6,
            received: enteredCode.length,
            code: enteredCode
          }
        });
        setLastResult('failure');
        return;
      }

      // Step 2: Database query
      await logEvent({
        layer: 'database',
        type: 'process',
        message: 'Querying database',
        details: {
          sql: `SELECT * FROM otp_codes WHERE email = ? AND code = ?`,
          bindings: [email, enteredCode],
          index: 'idx_otp_email_code'
        },
        duration: 100
      });

      // Step 3: Find matching record
      const record = mockDatabase.find(r =>
        r.email === email && r.code === enteredCode
      );

      await logEvent({
        layer: 'database',
        type: 'detail',
        message: record ? 'Record found' : 'No matching record',
        details: record ? {
          id: `otp-${Date.now()}`,
          email: record.email,
          codeMatches: record.code === enteredCode,
          expiresAt: record.expiresAt.toISOString(),
          used: record.used
        } : {
          searchCriteria: { email, code: enteredCode },
          recordsScanned: mockDatabase.length
        }
      });

      if (!record) {
        await logEvent({
          layer: 'result',
          type: 'error',
          message: '❌ Code not found',
          details: {
            reason: 'NO_MATCH',
            suggestion: 'Check email and code'
          }
        });

        const attempt: VerificationAttempt = {
          id: `attempt-${Date.now()}`,
          email,
          codeEntered: enteredCode,
          result: 'not_found',
          timestamp: new Date(),
          processingTime: Date.now() - startTime
        };
        setAttempts(prev => [...prev, attempt]);
        setLastResult('failure');
        return;
      }

      // Step 4: Constant-time comparison
      await logEvent({
        layer: 'comparison',
        type: 'process',
        message: 'Performing constant-time comparison',
        details: {
          method: 'crypto.timingSafeEqual',
          purpose: 'Prevent timing attacks',
          codeLength: 6
        },
        duration: 50
      });

      // Step 5: Check expiry
      const now = new Date();
      const isExpired = record.expiresAt < now;

      await logEvent({
        layer: 'expiry',
        type: isExpired ? 'warning' : 'info',
        message: 'Checking expiration',
        details: {
          currentTime: now.toISOString(),
          expiresAt: record.expiresAt.toISOString(),
          timeRemaining: isExpired ? 'EXPIRED' :
            `${Math.floor((record.expiresAt.getTime() - now.getTime()) / 1000)} seconds`,
          isExpired
        }
      });

      if (isExpired) {
        await logEvent({
          layer: 'result',
          type: 'error',
          message: '⏰ Code expired',
          details: {
            expiredAt: record.expiresAt.toISOString(),
            expiredAgo: `${Math.floor((now.getTime() - record.expiresAt.getTime()) / 1000)} seconds ago`
          }
        });

        const attempt: VerificationAttempt = {
          id: `attempt-${Date.now()}`,
          email,
          codeEntered: enteredCode,
          codeStored: record.code,
          result: 'expired',
          timestamp: new Date(),
          processingTime: Date.now() - startTime
        };
        setAttempts(prev => [...prev, attempt]);
        setLastResult('failure');
        return;
      }

      // Step 6: Check if already used
      if (record.used) {
        await logEvent({
          layer: 'validation',
          type: 'warning',
          message: 'Code already used',
          details: {
            usedAt: 'Previously',
            securityAction: 'REJECT'
          }
        });

        await logEvent({
          layer: 'result',
          type: 'error',
          message: '🚫 Code already used',
          details: {
            reason: 'ALREADY_USED',
            securityNote: 'One-time use only'
          }
        });

        const attempt: VerificationAttempt = {
          id: `attempt-${Date.now()}`,
          email,
          codeEntered: enteredCode,
          codeStored: record.code,
          result: 'used',
          timestamp: new Date(),
          processingTime: Date.now() - startTime
        };
        setAttempts(prev => [...prev, attempt]);
        setLastResult('failure');
        return;
      }

      // Step 7: Success!
      await logEvent({
        layer: 'result',
        type: 'success',
        message: '✅ VERIFICATION SUCCESSFUL',
        details: {
          email,
          code: enteredCode,
          validFor: `${Math.floor((record.expiresAt.getTime() - now.getTime()) / 1000)} more seconds`,
          processingTime: `${Date.now() - startTime}ms`
        }
      });

      const attempt: VerificationAttempt = {
        id: `attempt-${Date.now()}`,
        email,
        codeEntered: enteredCode,
        codeStored: record.code,
        result: 'success',
        timestamp: new Date(),
        processingTime: Date.now() - startTime
      };
      setAttempts(prev => [...prev, attempt]);
      setLastResult('success');

      // Step 8: Post-verification
      await logEvent({
        layer: 'database',
        type: 'info',
        message: 'Next step: Mark as used',
        details: {
          action: 'UPDATE otp_codes SET used = TRUE',
          purpose: 'Prevent reuse',
          willExecute: 'In production'
        }
      });

    } catch (error) {
      await logEvent({
        layer: 'result',
        type: 'error',
        message: 'Verification failed',
        details: { error: error instanceof Error ? error.message : 'Unknown error' }
      });
      setLastResult('failure');
    } finally {
      setIsVerifying(false);
    }
  };

  const simulateVariousScenarios = async () => {
    setEvents([]);

    const scenarios = [
      { email: 'test@example.com', code: '123456', expected: 'success' },
      { email: 'test@example.com', code: '654321', expected: 'invalid' },
      { email: 'expired@example.com', code: '999999', expected: 'expired' },
      { email: 'used@example.com', code: '111111', expected: 'already_used' },
      { email: 'wrong@example.com', code: '123456', expected: 'not_found' }
    ];

    for (const scenario of scenarios) {
      await logEvent({
        layer: 'validation',
        type: 'detail',
        message: `Testing: ${scenario.expected}`,
        details: scenario,
        duration: 100
      });
    }

    await logEvent({
      layer: 'result',
      type: 'success',
      message: 'All scenarios tested',
      details: {
        total: scenarios.length,
        scenarios: scenarios.map(s => s.expected)
      }
    });
  };

  const getLayerColor = (layer: string) => {
    const colors: Record<string, string> = {
      input: 'text-blue-400',
      validation: 'text-purple-400',
      database: 'text-yellow-400',
      comparison: 'text-green-400',
      expiry: 'text-orange-400',
      result: 'text-cyan-400'
    };
    return colors[layer] || 'text-gray-400';
  };

  const getLayerIcon = (layer: string) => {
    const icons: Record<string, string> = {
      input: '📝',
      validation: '✓',
      database: '🗄️',
      comparison: '🔐',
      expiry: '⏰',
      result: '🎯'
    };
    return icons[layer] || '•';
  };

  const getAttemptColor = (result: string) => {
    switch(result) {
      case 'success': return 'text-green-400 border-green-400';
      case 'expired': return 'text-orange-400 border-orange-400';
      case 'used': return 'text-yellow-400 border-yellow-400';
      case 'invalid':
      case 'not_found':
      default: return 'text-red-400 border-red-400';
    }
  };

  return (
    <div className="min-h-screen bg-black text-green-400 font-mono">
      <div className="max-w-7xl mx-auto p-4">
        {/* Header */}
        <div className="mb-6 border border-green-400 p-4">
          <h1 className="text-3xl mb-2">🔐 OTP VERIFICATION - PART 3</h1>
          <p className="text-gray-400">Check if code matches and is valid</p>
          <p className="text-yellow-400 text-sm mt-2">See every verification step</p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Left: Input and Controls */}
          <div className="space-y-4">
            {/* Input Section */}
            <div className="border border-green-400 p-4">
              <h2 className="text-xl mb-4 text-green-400">🔢 ENTER CODE</h2>

              <div className="space-y-4">
                <div>
                  <label className="text-gray-400 text-sm">Email Address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-gray-900 border border-gray-600 p-2 text-green-400 mt-1"
                    placeholder="user@example.com"
                  />
                </div>

                <div>
                  <label className="text-gray-400 text-sm">6-Digit Code</label>
                  <div className="flex gap-2 mt-2">
                    {[0, 1, 2, 3, 4, 5].map((index) => (
                      <input
                        key={index}
                        ref={el => codeInputRefs.current[index] = el}
                        type="text"
                        maxLength={1}
                        value={enteredCode[index] || ''}
                        onChange={(e) => handleCodeInput(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        className="w-12 h-12 text-center text-2xl bg-gray-900 border-2 border-gray-600 focus:border-green-400 text-green-400"
                      />
                    ))}
                  </div>
                  <div className="text-xs text-gray-500 mt-2">
                    Current: {enteredCode || '------'}
                  </div>
                </div>

                {lastResult && (
                  <div className={`p-4 border-2 ${
                    lastResult === 'success' ? 'border-green-400 bg-green-900/20' : 'border-red-400 bg-red-900/20'
                  }`}>
                    <div className={`text-xl font-bold ${
                      lastResult === 'success' ? 'text-green-400' : 'text-red-400'
                    }`}>
                      {lastResult === 'success' ? '✅ VALID CODE' : '❌ INVALID CODE'}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Controls */}
            <div className="border border-green-400 p-4">
              <h2 className="text-xl mb-4">🎮 CONTROLS</h2>

              <div className="space-y-2">
                <button
                  onClick={verifyOTP}
                  disabled={isVerifying || enteredCode.length !== 6}
                  className="w-full bg-green-400 text-black p-3 hover:bg-green-300 disabled:opacity-50 font-bold"
                >
                  {isVerifying ? '🔍 VERIFYING...' : '🔐 VERIFY CODE'}
                </button>

                <button
                  onClick={simulateVariousScenarios}
                  className="w-full bg-blue-400 text-black p-3 hover:bg-blue-300"
                >
                  🧪 TEST SCENARIOS
                </button>

                <button
                  onClick={() => {
                    setEnteredCode('123456');
                    setEmail('test@example.com');
                  }}
                  className="w-full bg-yellow-400 text-black p-3 hover:bg-yellow-300"
                >
                  ✓ FILL VALID CODE
                </button>

                <button
                  onClick={() => {
                    setEvents([]);
                    setEnteredCode('');
                    setLastResult(null);
                    setAttempts([]);
                  }}
                  className="w-full bg-gray-700 text-white p-3 hover:bg-gray-600"
                >
                  🔄 CLEAR ALL
                </button>
              </div>
            </div>

            {/* Test Codes */}
            <div className="border border-purple-400 p-4">
              <h2 className="text-xl mb-4 text-purple-400">🧪 TEST CODES</h2>
              <div className="text-xs space-y-2">
                <div className="border border-green-600 p-2">
                  <div className="text-green-400">✓ Valid Code</div>
                  <div className="text-gray-400">Email: test@example.com</div>
                  <div className="text-gray-400">Code: 123456</div>
                </div>
                <div className="border border-orange-600 p-2">
                  <div className="text-orange-400">⏰ Expired Code</div>
                  <div className="text-gray-400">Email: expired@example.com</div>
                  <div className="text-gray-400">Code: 999999</div>
                </div>
                <div className="border border-yellow-600 p-2">
                  <div className="text-yellow-400">🚫 Used Code</div>
                  <div className="text-gray-400">Email: used@example.com</div>
                  <div className="text-gray-400">Code: 111111</div>
                </div>
              </div>
            </div>

            {/* Attempt History */}
            {attempts.length > 0 && (
              <div className="border border-cyan-400 p-4">
                <h2 className="text-xl mb-4 text-cyan-400">📊 ATTEMPT HISTORY</h2>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {attempts.map((attempt, i) => (
                    <div key={attempt.id} className={`border p-2 text-xs ${getAttemptColor(attempt.result)}`}>
                      <div className="flex justify-between">
                        <span>#{attempts.length - i}</span>
                        <span>{attempt.result.toUpperCase()}</span>
                      </div>
                      <div className="text-gray-500 mt-1">
                        <div>Code: {attempt.codeEntered}</div>
                        <div>Time: {attempt.processingTime}ms</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: Event Stream */}
          <div className="border border-green-400 p-4">
            <h2 className="text-xl mb-4">📡 VERIFICATION EVENT STREAM</h2>

            <div className="h-[700px] overflow-y-auto space-y-2 text-xs font-mono">
              {events.length === 0 ? (
                <div className="text-gray-600 text-center py-8">
                  Waiting for verification events...
                </div>
              ) : (
                events.map(event => (
                  <div
                    key={event.id}
                    className={`border-l-2 pl-2 ${
                      event.type === 'success' ? 'border-green-400' :
                      event.type === 'error' ? 'border-red-400' :
                      event.type === 'warning' ? 'border-orange-400' :
                      event.type === 'process' ? 'border-blue-400' :
                      event.type === 'detail' ? 'border-purple-400' :
                      'border-gray-400'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-lg">{getLayerIcon(event.layer)}</span>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">
                            [{new Date(event.timestamp).toLocaleTimeString('en-US', {
                              hour12: false,
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit',
                              fractionalSecondDigits: 3
                            })}]
                          </span>
                          <span className={`font-bold ${getLayerColor(event.layer)}`}>
                            {event.layer.toUpperCase()}
                          </span>
                        </div>
                        <div className="text-gray-300 mt-1">{event.message}</div>
                        {event.details && (
                          <pre className="text-gray-500 mt-2 bg-gray-900 p-2 rounded overflow-x-auto">
{JSON.stringify(event.details, null, 2)}
                          </pre>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
              <div ref={eventsEndRef} />
            </div>
          </div>
        </div>

        {/* Bottom: Verification Complete */}
        {attempts.length > 0 && (
          <div className="mt-6 border border-cyan-400 p-4">
            <h2 className="text-xl text-cyan-400 mb-2">🎯 VERIFICATION INSIGHTS</h2>
            <p className="text-gray-400 mb-4">
              {attempts.length} verification attempts showing all failure modes and success paths.
              Each check involves database queries, expiry validation, and constant-time comparison.
            </p>
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-blue-400 font-bold">Input Validation:</span>
                <div className="text-gray-400">
                  • Format checking<br />
                  • Length validation<br />
                  • Email verification
                </div>
              </div>
              <div>
                <span className="text-yellow-400 font-bold">Database Query:</span>
                <div className="text-gray-400">
                  • Index lookup<br />
                  • Record retrieval<br />
                  • Status checking
                </div>
              </div>
              <div>
                <span className="text-orange-400 font-bold">Security Checks:</span>
                <div className="text-gray-400">
                  • Timing-safe compare<br />
                  • Expiry validation<br />
                  • One-time use
                </div>
              </div>
              <div>
                <span className="text-green-400 font-bold">Result:</span>
                <div className="text-gray-400">
                  • Success/Failure<br />
                  • Detailed reason<br />
                  • Next actions
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}